import React, { useState } from "react";
import type { Partner, Deal, Contact, Signal, Activity, Email, Todo, Minutes, Stage } from "../types";
import { STAGES } from "../types";
import { useData } from "../hooks/useData";
import { api } from "../api/client";
import { I } from "./Icons";
import {
  Avatar,
  clsx,
  fmtDate,
  relDue,
  dueText,
  dueOverdue,
  HeatPill,
  Money,
  Metric,
  Pill,
  SectionLabel,
  SignalTag,
  PartnerBadge,
  OWNER_LABEL,
} from "./Primitives";

/* ---- Inline icons used only by ProjectDetail ---- */

function Spark({ size = 16, w = 1.7 }: { size?: number; w?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.7 4.8L18.5 9.5 13.7 11.2 12 16l-1.7-4.8L5.5 9.5l4.8-1.7z" />
      <path d="M18.5 15.5l.7 2 .8-2 .8.7-2 .7 2 .8-.8.8-.8-2-.7 2z" opacity={0.7} />
    </svg>
  );
}

/* ---- Document type (not in global types) ---- */
interface DealDoc {
  id: string;
  name: string;
  kind: string;
  size: string;
  date: string;
}

/* ---- Props ---- */
interface ProjectDetailProps {
  id: string;
  go: (screen: string, id?: string) => void;
  openAI: (prefill?: string) => void;
  openMinutes: (prefill?: { dealId?: string }) => void;
  bump: () => void;
  partners: Record<string, Partner>;
}

/* ---- Sub-components ---- */

function ProbBar({ v }: { v: number }) {
  return (
    <span className="prob-bar">
      <span className="prob-fill" style={{ width: v + "%" }} />
    </span>
  );
}

function StageStepper({ stage, onChange }: { stage: Stage; onChange: (sid: Stage) => void }) {
  const stages = STAGES;
  const idx = stages.findIndex((s) => s.id === stage);

  const go = (i: number) => {
    if (i < 0 || i >= stages.length || i === idx) return;
    onChange(stages[i].id);
  };

  return (
    <div className="stage-stepper">
      <div className="ss-track">
        {stages.map((s, i) => {
          const state = i < idx ? "done" : i === idx ? "current" : "future";
          return (
            <React.Fragment key={s.id}>
              {i > 0 && <div className={clsx("ss-conn", i <= idx && "filled")} />}
              <button
                className={clsx("ss-step", state)}
                onClick={() => go(i)}
                title={i < idx ? "Move back to " + s.label : i > idx ? "Advance to " + s.label : s.label}
              >
                <span className="ss-dot">
                  {state === "done"
                    ? I.check({ size: 12, w: 2.6 })
                    : <span className="ss-num">{i + 1}</span>}
                </span>
                <span className="ss-label">{s.label}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>
      <div className="ss-actions">
        <button className="ss-btn" disabled={idx <= 0} onClick={() => go(idx - 1)}>
          {I.chevron({ size: 14, w: 2, style: { transform: "rotate(180deg)" } })}
          Back
        </button>
        {idx < stages.length - 1 ? (
          <button className="ss-btn primary" onClick={() => go(idx + 1)}>
            Advance to {stages[idx + 1].label}
            {I.arrowRight({ size: 14 })}
          </button>
        ) : (
          <span className="ss-final">
            {I.check({ size: 13, w: 2.4 })}
            Final stage
          </span>
        )}
      </div>
    </div>
  );
}

function Timeline({ acts, partners }: { acts: Activity[]; partners: Record<string, Partner> }) {
  const ICONS: Record<string, (p?: Record<string, unknown>) => React.ReactElement> = {
    meeting: I.meeting,
    email: I.mail,
    call: I.phone,
    note: I.note,
    stage: I.signal,
    signal: I.signal,
    minutes: I.note,
  };

  if (!acts.length) {
    return <div className="empty-mini" style={{ padding: "22px 20px" }}>Quiet so far — every great deal starts with a blank page.</div>;
  }

  return (
    <div className="timeline">
      {acts.map((a) => {
        const who = a.who ? partners[a.who] : null;
        const IC = ICONS[a.type] || I.note;
        return (
          <div key={a.id} className="tl-row">
            <div className={clsx("tl-ic", "tl-" + a.type)}>
              {IC({ size: 14, w: 1.8 })}
            </div>
            <div className="tl-body">
              <div className="tl-head">
                <span className="tl-title">{a.title}</span>
                <span className="tl-date mono">{fmtDate(a.date)}</span>
              </div>
              <div className="tl-text">{a.body}</div>
              {who && (
                <div className="tl-who">
                  <Avatar partner={who} size={18} />
                  {who.name}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DealTaskList({
  tasks,
  toggle,
  go,
  partners,
}: {
  tasks: Todo[];
  toggle: (id: string) => void;
  go: (screen: string, id?: string) => void;
  partners: Record<string, Partner>;
}) {
  if (!tasks.length) {
    return <div className="empty-mini" style={{ padding: "22px 20px" }}>No tasks yet — either this deal runs itself, or someone's being optimistic.</div>;
  }

  return (
    <div className="tasklist">
      {tasks.map((t) => {
        const who = t.owner === "all" || t.owner === "wait" ? null : partners[t.owner];
        const overdue = !t.done && dueOverdue(t);
        return (
          <div key={t.id} className={clsx("task-row", t.done && "done")}>
            <button
              className={clsx("checkbox", t.done && "checked")}
              onClick={() => toggle(t.id)}
            >
              {t.done && I.check({ size: 13, w: 2.4 })}
            </button>
            <span className="task-title">{t.task}</span>
            {t.priority === "high" && !t.done && <span className="task-pri">H&oslash;y</span>}
            <span className={clsx("task-due", overdue && "overdue")}>
              {t.due && I.clock({ size: 12 })}
              {dueText(t)}
            </span>
            {who ? (
              <Avatar partner={who} size={20} />
            ) : (
              <span className="ti-owner-tag">{OWNER_LABEL[t.owner] || "—"}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DealEmails({ mails }: { mails: Email[] }) {
  const STMETA: Record<string, [string, string]> = {
    auto: ["Routed by AI", "auto"],
    confirmed: ["Confirmed", "set"],
    manual: ["Allocated by you", "set"],
  };

  if (!mails.length) {
    return <div className="empty-mini" style={{ padding: "22px 20px" }}>No emails landed here yet. The AI is watching — it'll route them when they arrive.</div>;
  }

  return (
    <div className="deal-emails">
      {mails.map((e) => {
        const d = new Date(e.date);
        const day = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
        const init = (e.from_name || e.from_email || "?")
          .split(" ")
          .map((x) => x[0])
          .slice(0, 2)
          .join("");
        const m = STMETA[e.status] || ["Routed", "set"];
        const org = e.from_org || "External";
        return (
          <div key={e.id} className="de-row">
            <span className="de-av">{init}</span>
            <div className="de-body">
              <div className="de-top">
                <span className="de-from">{e.from_name}</span>
                <span className="de-org">{org}</span>
                <span className="de-when mono">{day}</span>
              </div>
              <div className="de-subj">{e.subject}</div>
              <div className="de-prev">{e.preview}</div>
            </div>
            <span className={clsx("de-chip", "de-" + m[1])}>
              {e.status === "auto" ? <Spark size={10} w={2} /> : I.check({ size: 10, w: 2.4 })}
              {m[0]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function DocList({ docs }: { docs: DealDoc[] }) {
  return (
    <div className="doclist">
      {docs.map((doc) => (
        <div key={doc.id} className="doc-row">
          <span className="doc-ic">{I.doc({ size: 16 })}</span>
          <span className="doc-name">{doc.name}</span>
          <span className="doc-kind">{doc.kind}</span>
          <span className="doc-meta mono">{doc.size}</span>
          <span className="doc-meta mono">{fmtDate(doc.date)}</span>
        </div>
      ))}
    </div>
  );
}

function MinutesList({ minutes, go }: { minutes: Minutes[]; go: (screen: string, id?: string) => void }) {
  if (!minutes.length) {
    return <div className="empty-mini" style={{ padding: "22px 20px" }}>No minutes yet. Had a meeting? Hit Cmd+M to capture it before you forget.</div>;
  }

  return (
    <div className="minutes-list">
      {minutes.map((m) => (
        <div key={m.id} className="tl-row" onClick={() => window.open(`/api/minutes/${m.id}/doc`, "_blank")}>
          <div className={clsx("tl-ic", "tl-minutes")}>
            {I.book({ size: 14, w: 1.8 })}
          </div>
          <div className="tl-body">
            <div className="tl-head">
              <span className="tl-title">{m.title}</span>
              <span className="tl-date mono">{fmtDate(m.date)}</span>
            </div>
            <div className="tl-text">{m.summary}</div>
            {m.attendees.length > 0 && (
              <div className="tl-who" style={{ opacity: 0.7 }}>
                {m.attendees.join(", ")}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ContactRow({ c, go }: { c: Contact; go: (screen: string, id?: string) => void }) {
  const init = c.name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("");
  return (
    <div
      className="contact-row clickable"
      onClick={() => go("clients", c.id)}
      title={"View communications with " + c.name}
    >
      <span className={clsx("c-avatar", "side-" + c.side)}>{init}</span>
      <div className="c-body">
        <div className="c-name">{c.name}</div>
        <div className="c-title">
          {c.title} &middot; {c.org_name || ""}
        </div>
      </div>
      <span className="contact-go">{I.mail({ size: 14 })}</span>
    </div>
  );
}

/* ---- Main Component ---- */

export default function ProjectDetail({ id, go, openAI, openMinutes, bump, partners }: ProjectDetailProps) {
  const { data: deal, refetch, setData } = useData<Deal>("/api/deals/" + id, [id]);
  const [tab, setTab] = useState("activity");

  if (!deal) {
    return <div className="screen proj"><div className="empty-mini" style={{ padding: 40 }}>Loading deal&hellip;</div></div>;
  }

  const d = deal;

  /* Derived data */
  const owner = d.owner ? partners[d.owner] : null;
  const contacts = d.contacts || [];
  const signals = d.signals || [];
  const activity = d.activity || [];
  const emails = (d.emails || []).filter((e) => e.status !== "private");
  const tasks = d.tasks || [];
  const docs = (d.documents || []) as unknown as DealDoc[];
  const minutes = d.minutes || [];
  const openTasks = tasks.filter((t) => !t.done).length;

  const inPartner = d.in_partner_id
    ? { name: d.in_partner_name || "", color: d.in_partner_color || "#888", role: d.in_partner_role }
    : null;

  /* Actions */
  const toggleTask = async (tid: string) => {
    const t = tasks.find((x) => x.id === tid);
    if (!t) return;
    await api.put("/api/todos/" + tid, { done: !t.done });
    refetch();
    bump();
  };

  const setStage = async (sid: Stage) => {
    const updates: Record<string, unknown> = { stage: sid };
    if (sid === "deployment" && d.probability < 80) updates.probability = 80;
    await api.put("/api/deals/" + id, updates);
    refetch();
    bump();
  };

  return (
    <div className="screen proj">
      {/* ---- Header ---- */}
      <div className="proj-head">
        <div className="crumbs">
          <a onClick={() => go("pipeline")}>Pipeline</a>
          {I.chevron({ size: 13, w: 2 })}
          <span>{d.market}</span>
        </div>

        <div className="proj-title-row">
          <h1 className="proj-title">{d.name}</h1>
          <div className="proj-head-actions">
            <HeatPill level={d.heat} />
            <button
              className="btn ghost"
              onClick={() => openMinutes({ dealId: d.id })}
            >
              {I.note({ size: 15, w: 1.8 })}
              Log minutes
            </button>
          </div>
        </div>

        <div className="proj-sub">
          <span className="rep-chip">{d.principal_name}</span>
          {I.arrowRight({ size: 15, w: 1.8, style: { color: "var(--faint)" } })}
          <span className="rep-chip op">{d.operator_name}</span>
          <span className="dotsep">&middot;</span>
          <span className="mkt">
            {I.globe({ size: 13, w: 1.7 })}
            {d.market}
          </span>
          {inPartner && <PartnerBadge partner={inPartner} />}
        </div>

        {/* ---- Interactive stage stepper ---- */}
        <StageStepper stage={d.stage} onChange={setStage} />

        {/* ---- Metric strip ---- */}
        <div className="metric-strip">
          <Metric k="Deal value" v={<Money v={d.value_m ?? 0} />} sub={d.model} />
          <Metric k="Probability" v={(d.probability ?? 0) + "%"} sub={<ProbBar v={d.probability ?? 0} />} />
          <Metric
            k="Expected close"
            v={d.close_date ? fmtDate(d.close_date, { day: "numeric", month: "short", year: "numeric" }) : "—"}
          />
          <Metric
            k="Owner"
            v={
              <span className="owner-inline">
                <Avatar partner={owner || null} size={22} />
                {owner ? owner.name.split(" ")[0] : "—"}
              </span>
            }
          />
        </div>
      </div>

      {/* ---- Burn banner ---- */}
      {(d.heat === "critical" || d.heat === "high") && (
        <div className={clsx("burn-banner", d.heat)}>
          <span className="burn-ic">{I.flame({ size: 15, w: 1.9 })}</span>
          <span className="burn-txt">
            <b>Why this is burning &mdash; </b>
            {d.heat_reason}
          </span>
          {d.next_due && (
            <span className="burn-due">
              {I.clock({ size: 13 })}
              Next: {relDue(d.next_due)}
            </span>
          )}
        </div>
      )}

      {/* ---- Body: two columns ---- */}
      <div className="proj-body">
        {/* ---- Main column ---- */}
        <div className="proj-main">
          {/* Thesis card */}
          <div className="card thesis">
            <SectionLabel>Deal thesis</SectionLabel>
            <p className="thesis-text">{d.thesis}</p>
            <div className="tags">
              {d.tags.map((t) => (
                <Pill key={t}>{t}</Pill>
              ))}
            </div>
          </div>

          {/* Tabbed content */}
          <div className="card">
            <div className="tabbar">
              {(["activity", "tasks", "emails", "documents", "minutes"] as const).map((t) => (
                <button
                  key={t}
                  className={clsx("tab", tab === t && "active")}
                  onClick={() => setTab(t)}
                >
                  {t === "activity"
                    ? "Activity"
                    : t === "tasks"
                    ? "Next steps"
                    : t === "emails"
                    ? "Emails"
                    : t === "documents"
                    ? "Documents"
                    : "Minutes"}
                  {t === "tasks" && openTasks > 0 && <span className="tab-count">{openTasks}</span>}
                  {t === "emails" && <span className="tab-count muted">{emails.length}</span>}
                  {t === "documents" && <span className="tab-count muted">{docs.length}</span>}
                  {t === "minutes" && <span className="tab-count muted">{minutes.length}</span>}
                </button>
              ))}
            </div>
            {tab === "activity" && <Timeline acts={activity} partners={partners} />}
            {tab === "tasks" && <DealTaskList tasks={tasks} toggle={toggleTask} go={go} partners={partners} />}
            {tab === "emails" && <DealEmails mails={emails} />}
            {tab === "documents" && <DocList docs={docs} />}
            {tab === "minutes" && <MinutesList minutes={minutes} go={go} />}
          </div>
        </div>

        {/* ---- Right rail ---- */}
        <div className="proj-rail">
          {/* Intelligence matches */}
          <div className="card rail-intel">
            <div className="rail-head">
              <SectionLabel>Saga intelligence</SectionLabel>
              <span className="rail-count">{signals.length} matched</span>
            </div>
            <div className="intel-list">
              {signals.length ? (
                signals.map((s) => (
                  <div key={s.id} className="intel-row" onClick={() => go("countries")}>
                    <div className="intel-top">
                      <SignalTag cls={s.cls} />
                      <span className={clsx("intel-sev", "sev-" + s.severity)} />
                      <span className="intel-date mono">{fmtDate(s.date)}</span>
                    </div>
                    <div className="intel-head">{s.headline}</div>
                    <div className="intel-src">{s.source}</div>
                  </div>
                ))
              ) : (
                <div className="empty-mini">No signals yet — the intel radar is scanning</div>
              )}
            </div>
            <button className="rail-more" onClick={() => go("countries")}>
              Open country updates
              {I.arrowRight({ size: 14 })}
            </button>
          </div>

          {/* Key contacts */}
          <div className="card">
            <div className="rail-head">
              <SectionLabel>Key contacts</SectionLabel>
              <button className="icon-btn">{I.plus({ size: 15 })}</button>
            </div>
            {contacts.length ? (
              <div className="contact-list">
                {contacts.map((c) => (
                  <ContactRow key={c.id} c={c} go={go} />
                ))}
              </div>
            ) : (
              <div className="empty-mini">No contacts linked — who's on the other side of this deal?</div>
            )}
          </div>

          {/* Market context */}
          <div className="card market-ctx">
            <SectionLabel>Market context &mdash; {d.market}</SectionLabel>
            <div className="ctx-row">
              <span className="ctx-k">Local content</span>
              <span className="ctx-v">
                {d.market === "Congo" ? "35% min (raised Q3 2026)" : "Active requirement"}
              </span>
            </div>
            <div className="ctx-row">
              <span className="ctx-k">Counterparty</span>
              <span className="ctx-v">{d.operator_type || "—"}</span>
            </div>
            <div className="ctx-row">
              <span className="ctx-k">Engagement</span>
              <span className="ctx-v">{d.model}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
